import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import MessagingService from '../../services/MessagingService';
import './MessagesPage.css';

const formatMessageTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const authorLabel = (message) => {
  if (message.authorLabel) {
    return message.authorLabel;
  }
  if (message.authorType === 'platform') {
    return 'Ritaaa';
  }
  if (message.authorType === 'user_account') {
    return 'Acheteur';
  }
  return 'Vous';
};

const bubbleClassName = (message) => {
  const classes = ['messages-bubble'];
  if (message.authorType === 'platform') {
    classes.push('is-platform');
  } else if (message.authorType === 'shop') {
    classes.push('is-own');
  }
  return classes.join(' ');
};

const OfficialMark = () => (
  <span className="messages-official" title="Compte officiel Ritaaa">
    <span className="messages-official-emoji" aria-hidden="true">🛡️</span>
    Officiel
  </span>
);

const MessagesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [archived, setArchived] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [body, setBody] = useState('');
  const [reason, setReason] = useState('');
  const [reportingId, setReportingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [composingSupport, setComposingSupport] = useState(false);
  const [supportBody, setSupportBody] = useState('');

  const loadInbox = async (showArchived = archived) => {
    setLoading(true);
    setError(null);
    try {
      const data = await MessagingService.list(showArchived);
      setThreads(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Impossible de charger les messages');
      setThreads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInbox(archived);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archived]);

  const supportQuery = searchParams.get('support');

  useEffect(() => {
    if (supportQuery !== '1') {
      setComposingSupport(false);
      return;
    }
    setComposingSupport(true);
    setSelectedId(null);
    setThread(null);
    setArchived(false);
    setError(null);
  }, [supportQuery]);

  const clearSupportQuery = () => {
    if (searchParams.get('support') !== '1') {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('support');
    setSearchParams(next, { replace: true });
  };

  const openThread = async (id) => {
    clearSupportQuery();
    setSelectedId(id);
    setError(null);
    setReportingId(null);
    setReason('');
    setComposingSupport(false);
    try {
      const data = await MessagingService.get(id);
      setThread(data);
      loadInbox(archived);
    } catch (err) {
      setError('Conversation introuvable');
      setThread(null);
    }
  };

  const handleSend = async (event) => {
    event.preventDefault();
    if (!selectedId || !body.trim()) {
      return;
    }
    try {
      await MessagingService.send(selectedId, body.trim());
      setBody('');
      await openThread(selectedId);
    } catch (err) {
      setError(err.response?.data?.error || 'Envoi impossible');
    }
  };

  const handleArchive = async () => {
    if (!selectedId) {
      return;
    }
    await MessagingService.archive(selectedId);
    setSelectedId(null);
    setThread(null);
    loadInbox(archived);
  };

  const handleReport = async (event, messageId) => {
    event.preventDefault();
    if (!selectedId || !reason.trim()) {
      return;
    }
    try {
      await MessagingService.report(selectedId, messageId, reason.trim());
      setReason('');
      setReportingId(null);
      await openThread(selectedId);
    } catch (err) {
      setError(err.response?.data?.error || 'Signalement impossible');
    }
  };

  const handleSupport = async (event) => {
    event.preventDefault();
    if (!supportBody.trim()) {
      return;
    }
    try {
      const data = await MessagingService.contactSupport(supportBody.trim());
      setSupportBody('');
      setComposingSupport(false);
      setArchived(false);
      clearSupportQuery();
      if (data?.id) {
        await openThread(data.id);
      } else {
        await loadInbox(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Envoi au support impossible');
    }
  };

  return (
    <div className="page-card messages-page">
      <h2>Messages</h2>
      <p>Mise en relation avec les acheteurs (disponibilité, sur-mesure, atelier). Ritaaa n’a pas encore de commandes. Pour écrire à l’équipe, utilisez « Contacter le support » en bas du menu.</p>
      <div className="messages-toolbar">
        <div className="messages-tabs">
        <button
          type="button"
          className={!archived ? 'is-active' : ''}
          onClick={() => {
            setArchived(false);
            setSelectedId(null);
            setThread(null);
            setComposingSupport(false);
            clearSupportQuery();
          }}
        >
          Boîte
        </button>
        <button
          type="button"
          className={archived ? 'is-active' : ''}
          onClick={() => {
            setArchived(true);
            setSelectedId(null);
            setThread(null);
            setComposingSupport(false);
            clearSupportQuery();
          }}
        >
          Archives
        </button>
        </div>
      </div>
      {error && <p className="messages-error">{error}</p>}
      <div className="messages-layout">
        <ul className="messages-list">
          {loading && <li>Chargement…</li>}
          {!loading && threads.length === 0 && <li>Aucun message.</li>}
          {threads.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className={`${item.id === selectedId ? 'is-active' : ''}${item.official ? ' is-official' : ''}`}
                onClick={() => openThread(item.id)}
              >
                <strong>
                  {item.label}
                  {item.official ? <OfficialMark /> : null}
                </strong>
                {item.unread ? <span className="messages-unread">non lu</span> : null}
                <span>{item.preview}</span>
              </button>
            </li>
          ))}
        </ul>
        <section className="messages-thread">
          {composingSupport && (
            <form onSubmit={handleSupport} className="messages-compose">
              <h3 className="messages-thread-title is-official">
                Support Ritaaa
                <OfficialMark />
              </h3>
              <textarea
                required
                maxLength={4000}
                value={supportBody}
                onChange={(event) => setSupportBody(event.target.value)}
                placeholder="Votre question à l’équipe Ritaaa (boutique, fiche, compte)"
              />
              <div className="messages-support-actions">
                <button type="submit" className="btn btn-primary">
                  Envoyer
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setComposingSupport(false);
                    setSupportBody('');
                    clearSupportQuery();
                  }}
                >
                  Annuler
                </button>
              </div>
            </form>
          )}
          {!composingSupport && !thread && <p>Sélectionnez une conversation.</p>}
          {!composingSupport && thread && (
            <>
              <div className="messages-thread-actions">
                {thread.label ? (
                  <h3 className={`messages-thread-title${thread.official ? ' is-official' : ''}`}>
                    {thread.label}
                    {thread.official ? <OfficialMark /> : null}
                  </h3>
                ) : null}
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleArchive}>
                  Archiver
                </button>
              </div>
              {(thread.messages || []).map((message) => {
                const canReport = !message.reported && message.authorType !== 'shop';
                return (
                <article key={message.id} className={bubbleClassName(message)}>
                  <header className="messages-meta">
                    <span className="messages-author">{authorLabel(message)}</span>
                    {message.authorType === 'platform' ? <OfficialMark /> : null}
                    <time className="messages-time" dateTime={message.createdAt}>
                      {formatMessageTime(message.createdAt)}
                    </time>
                    {message.reported ? (
                      <span className="messages-reported">Signalé</span>
                    ) : canReport && reportingId !== message.id ? (
                      <button
                        type="button"
                        className="messages-report-toggle"
                        onClick={() => setReportingId(message.id)}
                      >
                        Signaler
                      </button>
                    ) : null}
                  </header>
                  <p className="messages-body">{message.body}</p>
                  {canReport && reportingId === message.id ? (
                    <form
                      className="messages-report"
                      onSubmit={(event) => handleReport(event, message.id)}
                    >
                      <textarea
                        required
                        maxLength={500}
                        rows={2}
                        placeholder="Motif du signalement"
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                      />
                      <div className="messages-report-actions">
                        <button type="submit">Envoyer le signalement</button>
                        <button
                          type="button"
                          onClick={() => {
                            setReportingId(null);
                            setReason('');
                          }}
                        >
                          Annuler
                        </button>
                      </div>
                    </form>
                  ) : null}
                </article>
                );
              })}
              <form onSubmit={handleSend} className="messages-compose">
                <textarea
                  required
                  maxLength={4000}
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  placeholder="Votre réponse"
                />
                <button type="submit" className="btn btn-primary">
                  Répondre
                </button>
              </form>
              <p className="messages-legal">Les messages sont conservés 24 mois. Vous pouvez signaler un contenu illicite.</p>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default MessagesPage;
